import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate, useParams } from 'react-router-dom';
import { BarChart3, ListChecks, MessageSquare } from 'lucide-react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/Tabs';
import { Skeleton } from '@/components/ui/Skeleton';
import { queryKeys } from '@/lib/api/queryKeys';
import { eventsApi } from '@/features/events/api';
import { useT } from '@/lib/i18n/useT';
import { useFavoritable } from '@/features/favorites/FavoritableContext';
import { EditStationDialog } from './EditStationDialog';
import { StationHeader } from './components/StationHeader';
import { InventoryTab } from './components/InventoryTab';
import { ResponsesTab } from './components/ResponsesTab';
import { DashboardTab } from './components/DashboardTab';
import type { XkStation } from '@/lib/api/types';

export function StationDetailPage() {
  const { id } = useParams();
  const stationId = Number(id);
  const navigate = useNavigate();
  const t = useT();

  const summary = useQuery({
    queryKey: queryKeys.stations.summary(stationId),
    queryFn: () => eventsApi.getStationSummary(stationId),
  });
  useFavoritable(summary.data ? `Station · ${summary.data.station.name}` : null);

  const [editing, setEditing] = useState(false);

  if (summary.isLoading) {
    return (
      <div className="page space-y-4">
        <Skeleton className="h-8 w-40" />
        <Skeleton className="h-24 w-full" />
        <Skeleton className="h-72 w-full" />
      </div>
    );
  }
  if (!summary.data) {
    return <p className="page">{t('stations.detail.notFound')}</p>;
  }

  // Hydrate the shape EditStationDialog needs. Derived from the summary so
  // we don't make a second network call just for the edit modal.
  const stationEntity: XkStation = {
    id: summary.data.station.id,
    companyId: summary.data.station.companyId,
    status: summary.data.station.status as XkStation['status'],
    createdAt: summary.data.station.createdAt,
    updatedAt: summary.data.station.updatedAt,
    name: summary.data.station.name,
    eventId: summary.data.station.eventId,
    surveysId: summary.data.station.surveysId,
  };

  return (
    <div className="page space-y-6">
      <StationHeader summary={summary.data} onEdit={() => setEditing(true)} />

      <Tabs defaultValue="inventory">
        <TabsList data-tour="station-tabs">
          <TabsTrigger value="inventory" className="gap-2">
            <ListChecks className="h-4 w-4" />
            {t('stations.detail.inventoryTab')}
          </TabsTrigger>
          <TabsTrigger value="responses" className="gap-2">
            <MessageSquare className="h-4 w-4" />
            {t('stations.detail.responsesTab')}
          </TabsTrigger>
          <TabsTrigger value="dashboard" className="gap-2">
            <BarChart3 className="h-4 w-4" />
            {t('stations.detail.dashboardTab')}
          </TabsTrigger>
        </TabsList>

        <TabsContent value="inventory">
          <InventoryTab summary={summary.data} />
        </TabsContent>
        <TabsContent value="responses">
          <ResponsesTab
            stationId={stationId}
            surveyId={
              summary.data.responseSurveyIds[0] ??
              summary.data.station.surveysId
            }
          />
        </TabsContent>
        <TabsContent value="dashboard">
          <DashboardTab summary={summary.data} />
        </TabsContent>
      </Tabs>

      <EditStationDialog
        open={editing}
        onOpenChange={setEditing}
        station={stationEntity}
        onDeleted={() => navigate(-1)}
      />
    </div>
  );
}
